import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { CHART_MENU, getChartOptions } from '../../../utils';
import useEventListener from '../../PageEditor/hooks/useEventListener';
import Column from './components/Column';
import DraggableRow from './components/DraggableRow';
import Resizeable from './components/Resizeable';
import { styles } from './styles';

const ChartEditTable = ({ editorInfo, onUpdate, onClose }) => {
  const classes = styles();

  const tableRef = useRef(null);

  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState(0);
  const [editTableData, setEditTableData] = useState({
    isColHead: false,
    isRowHead: false,
    index: -1,
    row: -1,
  });
  const [isPieOrPolarChart, setPieOrPolarChart] = useState(false);

  const handleUpdate = (data) => {
    const obj = window.canvas.getActiveObject();
    const formattedData = {
      ...JSON.parse(obj.htmlContent),
      ...data,
    };
    const options = getChartOptions(formattedData);
    if (obj?.id) onUpdate({ id: obj.id, data: JSON.stringify(formattedData), options });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const updatedData = Array.from(tableData);
    const [movedRow] = updatedData.splice(sourceIndex, 1);
    updatedData.splice(destinationIndex, 0, movedRow);

    setTableData(updatedData);

    const data = {
      categories: columns,
      series: updatedData,
    };

    handleUpdate(data);
  };

  const handleEditTableData = (isColHead, isRowHead, index, row) => {
    setEditTableData({
      isColHead,
      isRowHead,
      index,
      row,
    });
  };

  useEffect(() => {
    if (editorInfo.data) {
      const { categories, series, selectedColumn, type } = JSON.parse(editorInfo.data);

      if (['polar', 'pie'].some((keyword) => CHART_MENU[type].title.toLowerCase().includes(keyword)))
        setPieOrPolarChart(true);
      else setPieOrPolarChart(false);

      setColumns(categories);
      setTableData(series);
      setSelectedColumn(selectedColumn);
    }
  }, [editorInfo.data]);

  const handleClickOutSide = (e) => {
    if (!tableRef.current?.contains(e.target) && e.target.tagName.toLowerCase() !== 'span') {
      setEditTableData({
        isColHead: false,
        isRowHead: false,
        index: -1,
        row: -1,
      });
    }
  };

  useEventListener('click', handleClickOutSide, document);

  return (
    <div className={classes.container}>
      <Resizeable onClose={onClose}>
        <table ref={tableRef} className={classes.table}>
          <thead>
            <tr>
              <th></th>
              {columns.map((col, index) => (
                <Column
                  key={index}
                  index={index}
                  col={col}
                  editorInfo={editorInfo}
                  editTableData={editTableData}
                  setEditTableData={setEditTableData}
                  handleEditTableData={handleEditTableData}
                  onUpdate={handleUpdate}
                  selectedColumn={selectedColumn}
                  isPieOrPolarChart={isPieOrPolarChart}
                />
              ))}
            </tr>
          </thead>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="table-row">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {tableData.length > 0 &&
                    tableData.map((row, index) => (
                      <DraggableRow
                        key={index}
                        index={index}
                        row={row}
                        editorInfo={editorInfo}
                        editTableData={editTableData}
                        setEditTableData={setEditTableData}
                        handleEditTableData={handleEditTableData}
                        onUpdate={handleUpdate}
                      />
                    ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </Resizeable>
    </div>
  );
};

export default ChartEditTable;
